import React, { useEffect, useState, useRef } from 'react';
import { Modal, Row, Col, Spinner, Badge, Button, Card, CardBody, Nav, NavItem, NavLink, TabContent, TabPane, Progress, Input, Form, Table, Alert } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import FeatherIcon from 'feather-icons-react';
import Select from 'react-select';
import { formatRoleName, reactiveStatusOptions, getWorkTypeBadgeColor } from '../constants/utils';
import api from '../helpers/api';
import { uploadServiceContractFiles, validateFiles } from '../helpers/fileUpload';
import AssignTechnicianModal from './AssignTechnicianModal';
import { translateActivity } from '../helpers/activityTranslator';
import AssetStatusChangeModal from './AssetStatusChangeModal';
import ServiceReportUploadModal from './ServiceReportUploadModal';
import ToolCheckoutModal from './ToolCheckoutModal';
import ToolCheckinModal from './ToolCheckinModal';
import usePermission from '../Hooks/usePermission';
import PermissionGuard from './Common/PermissionGuard';
import { WorkOrderStatusBadge } from './Common';
import StatusTimeline from './StatusTimeline';
import TimeEntrySection from './TimeEntrySection';
import WoCostSection from './WoCostSection';

const TicketDetailsModal = ({
  isOpen,
  toggle,
  ticketNumber,
  loading,
  formatCreatedDate,
  onStatusChange,
  fetchAssetIssueFromOrganization = null,
  onEdit = null,
  onAssetClick = null,
  workType = 'REACTIVE' // Default to REACTIVE for backward compatibility
}) => {
  /**
   * Get API endpoint prefix for fetching ticket data
   * @param {string} type - Work order type (REACTIVE, PREVENTIVE, etc.)
   * @returns {string} API endpoint prefix for data fetching
   */
  const getDataEndpointPrefix = (type) => {
    const endpoints = {
      'REACTIVE': 'asset-issues',
      'PREVENTIVE': 'asset-issues', // PREVENTIVE uses asset-issues for data
      'TESTING': 'testing-requests',
      'ROUTINE': 'routine-requests',
      'RECALL': 'recall',
      'CALIBRATION': 'calibration'
    };
    return endpoints[type?.toUpperCase()] || 'asset-issues';
  };

  /**
   * Get API endpoint prefix for activity and comments
   * @param {string} type - Work order type (REACTIVE, PREVENTIVE, etc.)
   * @returns {string} API endpoint prefix for activity/comments
   */
  const getActivityEndpointPrefix = (type) => {
    const endpoints = {
      'REACTIVE': 'asset-issues',
      'PREVENTIVE': 'asset-issues', // PREVENTIVE now uses asset-issues (unified)
      'TESTING': 'testing-requests',
      'ROUTINE': 'routine-requests',
      'RECALL': 'recall',
      'CALIBRATION': 'calibration'
    };
    return endpoints[type?.toUpperCase()] || 'asset-issues';
  };

  // Helper function to convert HH:MM:SS.mmm format back to decimal hours for display
  const convertTimeFormatToHours = (timeString) => {
    if (!timeString) return '';

    const timeParts = timeString.split(':');
    if (timeParts.length !== 3) return '';

    const hours = parseInt(timeParts[0]) || 0;
    const minutes = parseInt(timeParts[1]) || 0;
    const secondsAndMs = timeParts[2].split('.');
    const seconds = parseInt(secondsAndMs[0]) || 0;
    const milliseconds = parseInt(secondsAndMs[1]) || 0;

    return hours + (minutes / 60) + (seconds / 3600) + (milliseconds / 3600000);
  };
  const { t } = useTranslation();
  const { isRequestor, hasPermission } = usePermission();
  const [selectedStatus, setSelectedStatus] = useState('');
  const [ticketData, setTicketData] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [showAssignTechnicianModal, setShowAssignTechnicianModal] = useState(false);
  const [pendingStatusSelected, setPendingStatusSelected] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  // Spare Parts and Consumables States
  const [sparePartsUsage, setSparePartsUsage] = useState([]);
  const [consumablesUsage, setConsumablesUsage] = useState([]);
  const [loadingSparePartsConsumables, setLoadingSparePartsConsumables] = useState(false);

  // Add Spare Part to WO state
  const [addSparePartModal, setAddSparePartModal] = useState(false);
  const [availableSpareParts, setAvailableSpareParts] = useState([]);
  const [selectedSparePart, setSelectedSparePart] = useState(null);
  const [sparePartQuantity, setSparePartQuantity] = useState('');
  const [addingSparePartLoading, setAddingSparePartLoading] = useState(false);
  const [addSparePartError, setAddSparePartError] = useState('');

  // Attachments States
  const [showResolvedUploadModal, setShowResolvedUploadModal] = useState(false);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [deletingAttachment, setDeletingAttachment] = useState(null);
  const [attachmentErrors, setAttachmentErrors] = useState([]);
  const attachmentInputRef = useRef(null);

  // PM Chain History States
  const [chainHistory, setChainHistory] = useState([]);
  const [loadingChainHistory, setLoadingChainHistory] = useState(false);
  const [showAssetStatusChangeModal, setShowAssetStatusChangeModal] = useState(false);

  // Tools tab state
  const [requiredTools, setRequiredTools] = useState([]);
  const [checkedOutTools, setCheckedOutTools] = useState([]);
  const [loadingRequiredTools, setLoadingRequiredTools] = useState(false);
  const [loadingCheckedOutTools, setLoadingCheckedOutTools] = useState(false);
  const [newRequiredTool, setNewRequiredTool] = useState({ toolName: '', quantity: 1, notes: '' });
  const [toolCheckoutModal, setToolCheckoutModal] = useState(false);
  const [toolCheckinModal, setToolCheckinModal] = useState(false);
  const [toolCheckinId, setToolCheckinId] = useState(null);
  const [availableToolsSearch, setAvailableToolsSearch] = useState('');
  const [availableTools, setAvailableTools] = useState([]);
  const [showToolSearch, setShowToolSearch] = useState(false);
  const [selectedToolForCheckout, setSelectedToolForCheckout] = useState(null);

  useEffect(() => {
    if (isOpen && ticketNumber) {
      getData();
    }
  }, [isOpen, ticketNumber]);

  useEffect(() => {
    if (ticketData?.status) {
      setSelectedStatus(ticketData.status);
    }
  }, [ticketData]);

  useEffect(() => {
    if (ticketData?.id && activeTab === 'activity') {
      getActivity();
    }
  }, [ticketData?.id, activeTab, workType]);

  // Fetch spare parts and consumables when tabs are clicked
  useEffect(() => {
    if (ticketData?.id && (activeTab === 'spareParts' || activeTab === 'consumables')) {
      fetchSparePartsAndConsumables();
    }
  }, [ticketData?.id, activeTab]);

  // Fetch chain history when Occurrences tab is selected
  useEffect(() => {
    if (activeTab === 'occurrences' && ticketData?.id) {
      fetchChainHistory();
    }
  }, [activeTab, ticketData?.id]);

  // Fetch tools data when Tools tab is selected
  useEffect(() => {
    if (ticketData?.id && activeTab === 'tools') {
      fetchRequiredTools();
      fetchCheckedOutTools();
    }
  }, [ticketData?.id, activeTab]);

  const getData = async() => {
    try {
      const endpointPrefix = getDataEndpointPrefix(workType);
      const { data } = await api.get(`${endpointPrefix}/organization/filtered`, {
        params: {
          ticket: ticketNumber
        }
      });
      // Assuming the API returns an array and we want the first item
        setTicketData(data.response?.[0]);
    } catch (error) {
      console.error('Error fetching ticket details:', error);
      setTicketData(null);
    }
  }

  // Animation variants
  const modalVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.8,
      y: -50
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 25
      }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: 50,
      transition: {
        duration: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.3 }
    }
  };


  // Get priority badge
  const getPriorityBadge = (urgency) => {
    const priorityConfigs = {
      'HIGH': { color: 'danger', icon: 'alert-triangle' },
      'MEDIUM': { color: 'warning', icon: 'alert-circle' },
      'LOW': { color: 'success', icon: 'check-circle' }
    };

    const config = priorityConfigs[urgency] || { color: 'secondary', icon: 'help-circle' };
    
    return (
      <Badge 
        color={config.color}
        className="badge-soft d-inline-flex align-items-center"
        style={{ fontSize: '11px', padding: '4px 8px' }}
      >
        <FeatherIcon icon={config.icon} size={12} className="me-1" />
        {urgency ? t(`shared.${urgency.toLowerCase()}`, urgency.replace(/_/g, ' ')) : t('shared.not_set', 'Not Set')}
      </Badge>
    );
  };

  // Get progress for status
  const getStatusProgress = (status) => {
    const progressMap = {
      'SUBMITTED': 20,
      'PENDING': 40,
      'IN_PROGRESS': 60,
      'ON_HOLD': 30,
      'RESOLVED': 100
    };
    return progressMap[status] || 0;
  };

  const getActivity = async () => {
    try {
      setLoadingActivities(true);
      const endpointPrefix = getActivityEndpointPrefix(workType);

      // REACTIVE uses assertIssueId, others use parentId
      const paramName = workType?.toUpperCase() === 'REACTIVE' ? 'assertIssueId' : 'parentId';
      const apiUrl = `${endpointPrefix}/activity?${paramName}=${ticketData?.id}`;

      const { data } = await api.get(apiUrl);
      setActivities(data.response || []);
    } catch (error) {
      setActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  const handleStatusClick = async (value, id = null) => {
    if (value === 'PENDING') {
      setPendingStatusSelected(true);
      setShowAssignTechnicianModal(true);
    } else if (value === 'RESOLVED' && selectedStatus !== 'RESOLVED') {
      // Show optional upload prompt before marking as resolved
      setShowResolvedUploadModal(true);
    } else {
      setSelectedStatus(value);
      if (value !== ticketData.status) {
        await onStatusChange?.(ticketData, value, ticketData?.reportedBy?.id);
        await getActivity();
      }
    }
  };

  // Attachment handlers
  const handleResolveWithoutUpload = async () => {
    setShowResolvedUploadModal(false);
    setSelectedStatus('RESOLVED');
    if ('RESOLVED' !== ticketData.status) {
      await onStatusChange?.(ticketData, 'RESOLVED', ticketData?.reportedBy?.id);
      await getActivity();

    }
  };

  const handleResolveWithUpload = async (uploadedUrls) => {
    try {
      // Categorize uploaded files
      const newImageUrls = [];
      const newDocumentUrls = [];

      uploadedUrls.forEach(url => {
        if (/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(url)) {
          newImageUrls.push(url);
        } else {
          newDocumentUrls.push(url);
        }
      });

      // Update ticket with new attachments
      const payload = {
        id: ticketData.id,
        imageUrls: [...(ticketData.imageUrls || []), ...newImageUrls],
        documentUrls: [...(ticketData.documentUrls || []), ...newDocumentUrls]
      };

      await api.put('asset-issues/update', payload);

      // Close modal and resolve
      setShowResolvedUploadModal(false);
      setSelectedStatus('RESOLVED');
      await onStatusChange?.(ticketData, 'RESOLVED', ticketData?.reportedBy?.id);
      await getActivity();
      await getData(); // Refresh to show new attachments

    } catch (error) {
      console.error('Error uploading attachments:', error);
      // Still resolve even if upload fails
      setShowResolvedUploadModal(false);
      setSelectedStatus('RESOLVED');
      await onStatusChange?.(ticketData, 'RESOLVED', ticketData?.reportedBy?.id);
      await getActivity();

    }
  };

  const handleAttachmentUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Validate files before upload
    const validation = validateFiles(files);

    if (validation.errors.length > 0) {
      setAttachmentErrors(validation.errors);
      // Auto-clear errors after 5 seconds
      setTimeout(() => setAttachmentErrors([]), 5000);
    }

    // Only proceed if there are valid files
    if (validation.validFiles.length === 0) {
      // Reset file input
      if (attachmentInputRef.current) {
        attachmentInputRef.current.value = '';
      }
      return;
    }

    setUploadingAttachments(true);
    try {
      const uploadedUrls = await uploadServiceContractFiles(validation.validFiles);

      if (uploadedUrls && uploadedUrls.length > 0) {
        // Categorize uploaded files
        const newImageUrls = [];
        const newDocumentUrls = [];

        uploadedUrls.forEach(url => {
          if (/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(url)) {
            newImageUrls.push(url);
          } else {
            newDocumentUrls.push(url);
          }
        });

        // Update ticket with new attachments
        const payload = {
          id: ticketData.id,
          imageUrls: [...(ticketData.imageUrls || []), ...newImageUrls],
          documentUrls: [...(ticketData.documentUrls || []), ...newDocumentUrls]
        };

        await api.put('asset-issues/update', payload);
        await getData(); // Refresh to show new attachments
      }
    } catch (error) {
      console.error('Error uploading attachments:', error);
      setAttachmentErrors([t('shared.failed_upload', 'Failed to upload files. Please try again.')]);
      setTimeout(() => setAttachmentErrors([]), 5000);
    } finally {
      setUploadingAttachments(false);
      // Reset file input
      if (attachmentInputRef.current) {
        attachmentInputRef.current.value = '';
      }
    }
  };

  const dismissAttachmentError = (index) => {
    setAttachmentErrors(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeleteAttachment = async (url, type) => {
    setDeletingAttachment(url);
    try {
      const payload = { id: ticketData.id };

      if (type === 'image') {
        payload.imageUrls = (ticketData.imageUrls || []).filter(u => u !== url);
      } else {
        payload.documentUrls = (ticketData.documentUrls || []).filter(u => u !== url);
      }

      await api.put('asset-issues/update', payload);
      await getData(); // Refresh to update display
    } catch (error) {
      console.error('Error deleting attachment:', error);
    } finally {
      setDeletingAttachment(null);
    }
  };

  const getAttachmentCount = () => {
    return (ticketData?.imageUrls?.length || 0) + (ticketData?.documentUrls?.length || 0);
  };

  const getFileNameFromUrl = (url) => {
    try {
      const urlParts = url.split('/');
      return decodeURIComponent(urlParts[urlParts.length - 1]);
    } catch {
      return 'Document';
    }
  };

  const getDocumentIcon = (url) => {
    const ext = url.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return 'file-text';
    if (['doc', 'docx'].includes(ext)) return 'file-text';
    if (['xls', 'xlsx'].includes(ext)) return 'file-text';
    return 'file';
  };

  const handleAssignTechnician = async (id, technicianIds, teamIds = []) => {
    try {
      const techArray = Array.isArray(technicianIds) ? technicianIds : [technicianIds];
      const teamArray = Array.isArray(teamIds) ? teamIds : [teamIds];
      const isRemoving = techArray.length === 0 && teamArray.length === 0;

      // Only change status to PENDING when assigning, not when removing
      if (!isRemoving) {
        // Update status directly via API (onStatusChange has special handling for PENDING that prevents API call)
        await api.put('asset-issues/update', { id: id, status: 'PENDING' });
        setSelectedStatus('PENDING');
      }

      const payload = {
        id: id,
        assignedTechnician: techArray,
        assignedTeamIds: teamArray,
      };
      await api.put(`asset-issues/update`, payload);

      await getActivity();
      fetchAssetIssueFromOrganization && await fetchAssetIssueFromOrganization();
      getData();

      setShowAssignTechnicianModal(false);
      setPendingStatusSelected(false);
    } catch (error) {
      console.error('Error assigning technicians/teams:', error);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();

    if (!commentText.trim()) {
      return;
    }

    try {
      setSubmittingComment(true);
      const endpointPrefix = getActivityEndpointPrefix(workType);
      const payload = {
        parentId: ticketData?.id,
        activity: commentText.trim()
      };

      await api.patch(`${endpointPrefix}/comment`, payload);

      // Clear the comment input
      setCommentText('');

      // Refresh activities to show the new comment
      await getActivity();
    } catch (error) {
      console.error('Error submitting comment:', error);
      // You might want to show an error toast here
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleCommentKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCommentSubmit(e);
    }
  };

  // Fetch spare parts and consumables usage
  const fetchSparePartsAndConsumables = async () => {
    if (!ticketData?.id) return;

    try {
      setLoadingSparePartsConsumables(true);
      const response = await api.get(`asset-issues/spare-part-consumable-usage?serviceId=${ticketData.id}`);

      setSparePartsUsage(response?.data?.response?.sparePartUsage || []);
      setConsumablesUsage(response?.data?.response?.consumablesUsage || []);
    } catch (error) {
      console.error('Error fetching spare parts and consumables:', error);
      setSparePartsUsage([]);
      setConsumablesUsage([]);
    } finally {
      setLoadingSparePartsConsumables(false);
    }
  };

  // Fetch available spare parts for the add parts dropdown
  const fetchAvailableSpareParts = async () => {
    try {
      const { data } = await api.get('spare-parts/all');
      setAvailableSpareParts(data?.response || []);
    } catch (error) {
      console.error('Error fetching available spare parts:', error);
      setAvailableSpareParts([]);
    }
  };

  // Handle adding a spare part to this work order
  const handleAddSparePart = async () => {
    if (!selectedSparePart || !sparePartQuantity || Number(sparePartQuantity) <= 0) {
      setAddSparePartError(t('business.spare_parts.select_part_and_quantity', 'Please select a spare part and enter a valid quantity'));
      return;
    }

    setAddingSparePartLoading(true);
    setAddSparePartError('');

    try {
      const payload = {
        serviceId: ticketData.id,
        inventory: [
          {
            sparePartId: selectedSparePart.value,
            quantity: Number(sparePartQuantity),
            status: 'APPROVED'
          }
        ]
      };

      const apiResponse = await api.post('spare-parts/inventory-consumption', payload);

      // Backend returns 200 even when items are skipped (e.g. no available inventory).
      // The inner envelope tells us whether anything was actually created.
      const inner = apiResponse?.data?.response;
      const created = Array.isArray(inner?.response) ? inner.response : [];
      const warnings = Array.isArray(inner?.warning) ? inner.warning : [];

      if (created.length === 0) {
        setAddSparePartError(
          warnings.join(' ') ||
          inner?.message ||
          t('business.spare_parts.add_error', 'Failed to add spare part')
        );
        return;
      }

      // Reset form and close modal
      setSelectedSparePart(null);
      setSparePartQuantity('');
      setAddSparePartModal(false);

      // Refresh the spare parts list
      await fetchSparePartsAndConsumables();
    } catch (error) {
      console.error('Error adding spare part:', error);
      setAddSparePartError(error.response?.data?.message || t('business.spare_parts.add_error', 'Failed to add spare part'));
    } finally {
      setAddingSparePartLoading(false);
    }
  };

  // ================== Sub Work Order Functions ==================

  // ================== PM Chain History ==================

  // Fetch chain history for preventive work orders
  const fetchChainHistory = async () => {
    if (!ticketData?.id) return;

    try {
      setLoadingChainHistory(true);
      const response = await api.get(`asset-issues/${ticketData.id}/chain-history`);
      setChainHistory(response?.data?.response || []);
    } catch (error) {
      console.error('Error fetching chain history:', error);
      setChainHistory([]);
    } finally {
      setLoadingChainHistory(false);
    }
  };

  // Tools tab functions
  const fetchRequiredTools = async () => {
    if (!ticketData?.id) return;
    try {
      setLoadingRequiredTools(true);
      const { data } = await api.get(`work-order-tool/required/all?workOrderId=${ticketData.id}`);
      setRequiredTools(data?.response || []);
    } catch (error) {
      console.error('Error fetching required tools:', error);
      setRequiredTools([]);
    } finally {
      setLoadingRequiredTools(false);
    }
  };

  const fetchCheckedOutTools = async () => {
    if (!ticketData?.id) return;
    try {
      setLoadingCheckedOutTools(true);
      const { data } = await api.get(`work-order-tool/by-work-order?workOrderId=${ticketData.id}`);
      setCheckedOutTools(data?.response || []);
    } catch (error) {
      console.error('Error fetching checked out tools:', error);
      setCheckedOutTools([]);
    } finally {
      setLoadingCheckedOutTools(false);
    }
  };

  const handleAddRequiredTool = async () => {
    if (!newRequiredTool.toolName.trim()) return;
    try {
      await api.post('work-order-tool/required/add', [{
        workOrderId: ticketData.id,
        toolName: newRequiredTool.toolName.trim(),
        quantity: parseInt(newRequiredTool.quantity) || 1,
        notes: newRequiredTool.notes || ''
      }]);
      setNewRequiredTool({ toolName: '', quantity: 1, notes: '' });
      await fetchRequiredTools();
    } catch (error) {
      console.error('Error adding required tool:', error);
    }
  };

  const handleDeleteRequiredTool = async (id) => {
    try {
      await api.delete(`work-order-tool/required/delete?id=${id}`);
      await fetchRequiredTools();
    } catch (error) {
      console.error('Error deleting required tool:', error);
    }
  };

  const fetchAvailableToolsForCheckout = async (search) => {
    try {
      const { data } = await api.get(`tool/all?status=AVAILABLE&search=${encodeURIComponent(search)}&count=20`);
      setAvailableTools(data?.response || []);
    } catch (error) {
      console.error('Error fetching available tools:', error);
      setAvailableTools([]);
    }
  };

  const handleToolSearchChange = (value) => {
    setAvailableToolsSearch(value);
    if (value.length >= 2) {
      fetchAvailableToolsForCheckout(value);
      setShowToolSearch(true);
    } else {
      setShowToolSearch(false);
      setAvailableTools([]);
    }
  };

  const handleSelectToolForCheckout = (tool) => {
    setSelectedToolForCheckout(tool);
    setShowToolSearch(false);
    setAvailableToolsSearch('');
    setToolCheckoutModal(true);
  };

  const handleToolCheckoutSuccess = async () => {
    setToolCheckoutModal(false);
    setSelectedToolForCheckout(null);
    await fetchCheckedOutTools();
  };

  const handleToolCheckinFromWO = (checkoutId) => {
    setToolCheckinId(checkoutId);
    setToolCheckinModal(true);
  };

  const handleToolCheckinSuccess = async () => {
    setToolCheckinModal(false);
    setToolCheckinId(null);
    await fetchCheckedOutTools();
  };

  const formatToolDate = (date) => {
    if (!date) return '-';
    if (Array.isArray(date)) {
      const [y, m, d] = date;
      return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
    }
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return date;
      return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    } catch {
      return date;
    }
  };

  // Check if this is a preventive occurrence with recurrence (workType may be "Preventive" from FieldFormatter or "PREVENTIVE" raw)
  const isPreventiveRecurring = ticketData?.workType?.toUpperCase() === 'PREVENTIVE' && ticketData?.recurrenceType && ticketData?.recurrenceType !== 'NONE';
  const isCompletedOccurrence = ticketData?.status === 'RESOLVED';

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <Modal
            isOpen={isOpen}
            toggle={toggle}
            size="xl"
            centered
            className="modern-modal"
            backdrop="static"
            style={{ maxHeight: '90vh' }}
          >
            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="modal-content border-0 shadow-lg"
              style={{ borderRadius: '12px', overflow: 'hidden' }}
            >
              {/* Enhanced Header */}
              <div
                className="modal-header border-0 text-white position-sticky d-flex justify-content-between align-items-center"
                style={{
                  top: 0,
                  zIndex: 10,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  padding: '1.5rem'
                }}
              >
                <div className="d-flex align-items-center">
                  <div className="ticket-icon me-3">
                    <div className="bg-white bg-opacity-20 rounded-circle d-flex align-items-center justify-content-center"
                         style={{ width: '48px', height: '48px' }}>
                      <FeatherIcon icon="alert-circle" size={24} className="text-white" />
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <h4 className="modal-title mb-1 text-white d-flex align-items-center">
                      <span className="me-2">{t('shared.ticket', 'Ticket')} # {ticketData?.ticketNumber}</span>
                       <div className="d-flex align-items-center gap-2">
                      <WorkOrderStatusBadge status={selectedStatus} />
                    {getPriorityBadge(ticketData?.urgencyLevel)}
                  </div>
                    </h4>
                    <div className="d-flex align-items-center gap-3">
                      <span className="text-white small">
                        <FeatherIcon icon="calendar" size={14} className="me-1" />
                        {formatCreatedDate(ticketData?.createdAt)}
                      </span>
                      <span className="text-white small">
                        <FeatherIcon icon="user" size={14} className="me-1" />
                        {ticketData?.reportedBy?.name}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-3 ms-auto">
                  {!isRequestor && onEdit && ticketData && (
                    <button
                      type="button"
                      className="btn btn-sm d-flex align-items-center gap-2 px-3 py-2 border-0 shadow-sm"
                      onClick={() => onEdit(ticketData)}
                       title={t('shared.buttons_edit_ticket', 'Edit Ticket')}
                      style={{
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)',
                        color: '#495057',
                        fontWeight: '500',
                        borderRadius: '8px',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 1)';
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.95)';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                      }}
                    >
                      <FeatherIcon icon="edit-3" size={16} />
                      <span>{t('shared.edit', 'Edit')}</span>
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={toggle}
                    aria-label={t('shared.close', 'Close')}
                  ></button>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="px-4 pt-3 bg-light">
                <div className="d-flex align-items-center justify-content-between mb-2" style={{ width: '35%' }}>
                  <small className="text-muted fw-semibold">{t('shared.progress', 'Progress')}</small>
                  <small className="text-muted">{getStatusProgress(selectedStatus)}%</small>
                </div>
                <Progress 
                  value={getStatusProgress(selectedStatus)} 
                  color={selectedStatus === 'RESOLVED' ? 'success' : 'primary'}
                  style={{ height: '6px',width:'35%' }}
                  className="mb-3"
                />
              </div>

              {/* Enhanced Navigation */}
              <Nav 
                tabs 
                className="border-bottom-0 px-4 bg-light"
              >
                <NavItem>
                  <NavLink
                    className={`nav-link-modern ${activeTab === 'details' ? 'active' : ''}`}
                    onClick={() => setActiveTab('details')}
                    style={{
                      border: 'none',
                      borderRadius: '8px 8px 0 0',
                      padding: '12px 20px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                  >
                    <FeatherIcon icon="file-text" size={16} className="me-2" />
                    {t('shared.details', 'Details')}
                  </NavLink>
                </NavItem>
                {!isRequestor && (
                  <NavItem>
                    <NavLink
                      className={`nav-link-modern ${activeTab === 'management' ? 'active' : ''}`}
                      onClick={() => setActiveTab('management')}
                      style={{
                        border: 'none',
                        borderRadius: '8px 8px 0 0',
                        padding: '12px 20px',
                        fontWeight: '500',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer'
                      }}
                    >
                      <FeatherIcon icon="settings" size={16} className="me-2" />
                      {t('shared.management', 'Management')}
                    </NavLink>
                  </NavItem>
                )}
                <NavItem>
                  <NavLink
                    className={`nav-link-modern ${activeTab === 'attachments' ? 'active' : ''}`}
                    onClick={() => setActiveTab('attachments')}
                    style={{
                      border: 'none',
                      borderRadius: '8px 8px 0 0',
                      padding: '12px 20px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                  >
                    <FeatherIcon icon="paperclip" size={16} className="me-2" />
                    {t('shared.media', 'Media')}
                    {getAttachmentCount() > 0 && (
                      <Badge color="info" pill className="ms-2" style={{ fontSize: '10px' }}>
                        {getAttachmentCount()}
                      </Badge>
                    )}
                  </NavLink>
                </NavItem>
                {!isRequestor && (
                  <NavItem>
                    <NavLink
                      className={`nav-link-modern ${activeTab === 'spareParts' ? 'active' : ''}`}
                      onClick={() => setActiveTab('spareParts')}
                      style={{
                        border: 'none',
                        borderRadius: '8px 8px 0 0',
                        padding: '12px 20px',
                        fontWeight: '500',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer'
                      }}
                    >
                      <FeatherIcon icon="tool" size={16} className="me-2" />
                      {t('shared.spare_parts', 'Spare Parts')}
                      {sparePartsUsage.length > 0 && (
                        <Badge color="primary" pill className="ms-2" style={{ fontSize: '10px' }}>
                          {sparePartsUsage.length}
                        </Badge>
                      )}
                    </NavLink>
                  </NavItem>
                )}
                {!isRequestor && ['REACTIVE', 'PREVENTIVE'].includes((ticketData?.workType || workType)?.toUpperCase()) && (
                  <NavItem>
                    <NavLink
                      className={`nav-link-modern ${activeTab === 'tools' ? 'active' : ''}`}
                      onClick={() => setActiveTab('tools')}
                      style={{
                        border: 'none',
                        borderRadius: '8px 8px 0 0',
                        padding: '12px 20px',
                        fontWeight: '500',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer'
                      }}
                    >
                      <FeatherIcon icon="box" size={16} className="me-2" />
                      {t('shared.tools', 'Tools')}
                      {(requiredTools.length + checkedOutTools.length) > 0 && (
                        <Badge color="warning" pill className="ms-2" style={{ fontSize: '10px' }}>
                          {requiredTools.length + checkedOutTools.length}
                        </Badge>
                      )}
                    </NavLink>
                  </NavItem>
                )}
                {/* Consumables tab - commented out
                {!isRequestor && (
                  <NavItem>
                    <NavLink
                      className={`nav-link-modern ${activeTab === 'consumables' ? 'active' : ''}`}
                      onClick={() => setActiveTab('consumables')}
                      style={{
                        border: 'none',
                        borderRadius: '8px 8px 0 0',
                        padding: '12px 20px',
                        fontWeight: '500',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer'
                      }}
                    >
                      <FeatherIcon icon="package" size={16} className="me-2" />
                      Consumables
                      {consumablesUsage.length > 0 && (
                        <Badge color="secondary" pill className="ms-2" style={{ fontSize: '10px' }}>
                          {consumablesUsage.length}
                        </Badge>
                      )}
                    </NavLink>
                  </NavItem>
                )}
                */}
                {isPreventiveRecurring && (
                  <NavItem>
                    <NavLink
                      className={`nav-link-modern ${activeTab === 'occurrences' ? 'active' : ''}`}
                      onClick={() => setActiveTab('occurrences')}
                      style={{
                        border: 'none',
                        borderRadius: '8px 8px 0 0',
                        padding: '12px 20px',
                        fontWeight: '500',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer'
                      }}
                    >
                      <FeatherIcon icon="repeat" size={16} className="me-2" />
                      {t('shared.occurrences', 'Occurrences')}
                      {ticketData?.recurrenceNumber && (
                        <Badge color="primary" pill className="ms-2" style={{ fontSize: '10px' }}>
                          #{ticketData.recurrenceNumber}
                        </Badge>
                      )}
                    </NavLink>
                  </NavItem>
                )}
                <NavItem>
                  <NavLink
                    className={`nav-link-modern ${activeTab === 'timeAndCost' ? 'active' : ''}`}
                    onClick={() => setActiveTab('timeAndCost')}
                    style={{
                      border: 'none',
                      borderRadius: '8px 8px 0 0',
                      padding: '12px 20px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                  >
                    <FeatherIcon icon="clock" size={16} className="me-2" />
                    {t('cost_tracking.time_and_cost_tab', 'Time & Cost')}
                  </NavLink>
                </NavItem>
                {!isRequestor && (
                  <NavItem>
                    <NavLink
                      className={`nav-link-modern ${activeTab === 'activity' ? 'active' : ''}`}
                      onClick={() => setActiveTab('activity')}
                      style={{
                        border: 'none',
                        borderRadius: '8px 8px 0 0',
                        padding: '12px 20px',
                        fontWeight: '500',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer'
                      }}
                    >
                      <FeatherIcon icon="activity" size={16} className="me-2" />
                      {t('shared.activity', 'Activity')}
                      {activities.length > 0 && (
                        <Badge color="secondary" pill className="ms-2" style={{ fontSize: '10px' }}>
                          {activities.length}
                        </Badge>
                      )}
                    </NavLink>
                  </NavItem>
                )}
              </Nav>

              {/* Modal Body */}
              <div 
                className="modal-body p-0" 
                style={{ 
                  maxHeight: 'calc(90vh - 280px)',
                  overflow: 'auto'
                }}
              >
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" size="lg" />
                    <p className="text-muted mt-3">Loading ticket details...</p>
                  </div>
                ) : (
                  <TabContent activeTab={activeTab} className="h-100">
                    {/* Details Tab */}
                    <TabPane tabId="details" className="h-100">
                      <div className="p-4">
                        {/* Ticket Information Cards */}
                        <Row className="g-4 mb-4">
                          {/* Basic Information */}
                          <Col md={6}>
                            <motion.div variants={cardVariants} initial="hidden" animate="visible" className="h-100">
                              <Card className="border-0 shadow-sm h-100">
                                <CardBody className="p-4">
                                  <div className="d-flex align-items-center mb-3">
                                    <div className="info-icon me-3">
                                      <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                           style={{ width: '40px', height: '40px' }}>
                                        <FeatherIcon icon="info" size={20} className="text-primary" />
                                      </div>
                                    </div>
                                    <div>
                                      <h6 className="mb-1 fw-bold">{t('shared.basic_information', 'Basic Information')}</h6>
                                      <p className="text-muted small mb-0">{t('business.components.ticket_details_modal.sections.basic_information_sub', 'Core ticket details')}</p>
                                    </div>
                                  </div>

                                  <div className="info-grid">
                                    <div className="info-item mb-3">
                                      <div className="d-flex align-items-center mb-1">
                                        <FeatherIcon icon="hash" size={14} className="text-muted me-2" />
                                        <span className="fw-semibold text-muted small">{t('shared.ticket_number', 'Ticket Number')}</span>
                                      </div>
                                      <p className="mb-0 text-dark">{ticketData?.ticketNumber}</p>
                                    </div>

                                    <div className="info-item mb-3">
                                      <div className="d-flex align-items-center mb-1">
                                        <FeatherIcon icon="hash" size={14} className="text-muted me-2" />
                                        <span className="fw-semibold text-muted small">{t('business.components.ticket_details_modal.fields.ticket_title', 'Ticket Title')}</span>
                                      </div>
                                      <p className="mb-0 text-dark">{ticketData?.title}</p>
                                    </div>

                                    <div className="info-item mb-3">
                                      <div className="d-flex align-items-center mb-1">
                                        <FeatherIcon icon="calendar" size={14} className="text-muted me-2" />
                                        <span className="fw-semibold text-muted small">{t('shared.first_occurrence', 'First Occurrence')}</span>
                                      </div>
                                      <p className="mb-0 text-dark">{formatCreatedDate(ticketData?.firstOccurrence)}</p>
                                    </div>

                                    <div className="info-item mb-0">
                                      <div className="d-flex align-items-center mb-1">
                                        <FeatherIcon icon="calendar" size={14} className="text-muted me-2" />
                                        <span className="fw-semibold text-muted small">{t('shared.due_date', 'Due Date')}</span>
                                      </div>
                                      <p className="mb-0 text-dark">
                                        {ticketData?.dueDate
                                          ? formatCreatedDate(ticketData.dueDate)
                                          : ''
                                        }
                                      </p>
                                    </div>
                                  </div>
                                </CardBody>
                              </Card>
                            </motion.div>
                          </Col>

                          {/* Issue Details */}
                          <Col md={6}>
                            <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.1 }} className="h-100">
                              <Card className="border-0 shadow-sm h-100">
                                <CardBody className="p-4">
                                  <div className="d-flex align-items-center mb-3">
                                    <div className="info-icon me-3">
                                      <div className="bg-warning bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                           style={{ width: '40px', height: '40px' }}>
                                        <FeatherIcon icon="alert-triangle" size={20} className="text-warning" />
                                      </div>
                                    </div>
                                    <div>
                                      <h6 className="mb-1 fw-bold">{t('business.components.ticket_details_modal.sections.issue_details', 'Issue Details')}</h6>
                                      <p className="text-muted small mb-0">{t('business.components.ticket_details_modal.sections.issue_details_sub', 'Problem description and symptoms')}</p>
                                    </div>
                                  </div>

                                  <div className="info-grid">
                                    <div className="info-item mb-3">
                                      <div className="d-flex align-items-center mb-1">
                                        <FeatherIcon icon="tool" size={14} className="text-muted me-2" />
                                        <span className="fw-semibold text-muted small">{t('shared.work_type', 'Work Type')}</span>
                                      </div>
                                      <div className="d-flex flex-wrap gap-1">
                                        <Badge color={getWorkTypeBadgeColor(ticketData?.workType)} className="text-white text-uppercase">
                                          {t(`shared.${(ticketData?.workType || 'OTHER').toLowerCase()}`, ticketData?.workType?.replace(/_/g, ' ') || 'Other')}
                                        </Badge>
                                      </div>
                                    </div>

                                    <div className="info-item mb-3">
                                      <div className="d-flex align-items-center mb-1">
                                        <FeatherIcon icon="zap" size={14} className="text-muted me-2" />
                                        <span className="fw-semibold text-muted small">{t('shared.urgency_level', 'Urgency Level')}</span>
                                      </div>
                                      <div className="text-uppercase">{getPriorityBadge(ticketData?.urgencyLevel)}</div>
                                    </div>

                                    <div className="info-item mb-3">
                                      <div className="d-flex align-items-center mb-1">
                                        <FeatherIcon icon="clock" size={14} className="text-muted me-2" />
                                        <span className="fw-semibold text-muted small">{t('shared.estimated_time', 'Estimated Time')}</span>
                                      </div>
                                      <p className="mb-0 text-dark">
                                        {ticketData?.estimatedTime
                                          ? t('business.components.ticket_details_modal.hours_count', '{{count}} hours', { count: convertTimeFormatToHours(ticketData.estimatedTime).toFixed(1) })
                                          : t('shared.not_specified', 'Not specified')
                                        }
                                      </p>
                                    </div>

                                    <div className="info-item mb-0">
                                      <div className="d-flex align-items-center mb-1">
                                        <FeatherIcon icon="activity" size={14} className="text-muted me-2" />
                                        <span className="fw-semibold text-muted small">{t('shared.usage_status', 'Usage Status')}</span>
                                      </div>
                                      <p className="mb-0 text-dark">{ticketData?.usageStatus?.replace(/_/g, ' ') || t('shared.not_specified', 'Not specified')}</p>
                                    </div>
                                  </div>
                                </CardBody>
                              </Card>
                            </motion.div>
                          </Col>
                        </Row>

                        {/* Asset and Location Information */}
                        <Row className="g-4 mb-4">
                          <Col md={6}>
                            <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.2 }}>
                              <Card className="border-0 shadow-sm">
                                <CardBody className="p-4">
                                  <div className="d-flex align-items-center mb-3">
                                    <div className="info-icon me-3">
                                      <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                           style={{ width: '40px', height: '40px' }}>
                                        <FeatherIcon icon="package" size={20} className="text-success" />
                                      </div>
                                    </div>
                                    <div>
                                      <h6 className="mb-1 fw-bold">{t('shared.asset_details', 'Asset Details')}</h6>
                                      <p className="text-muted small mb-0">{t('shared.equipment_information', 'Equipment information')}</p>
                                    </div>
                                  </div>

                                  <div className="info-grid">
                                    <div className="info-item mb-3">
                                      <div className="d-flex align-items-center mb-1">
                                        <FeatherIcon icon="box" size={14} className="text-muted me-2" />
                                        <span className="fw-semibold text-muted small">{t('shared.asset_name', 'Asset Name')}</span>
                                      </div>
                                      <p className="mb-0 text-dark">{ticketData?.asset?.assetName || t('shared.not_specified', 'Not specified')}</p>
                                    </div>

                                    <div className="info-item mb-3">
                                      <div className="d-flex align-items-center mb-1">
                                        <FeatherIcon icon="hash" size={14} className="text-muted me-2" />
                                        <span className="fw-semibold text-muted small">{t('shared.asset_id', 'Asset ID')}</span>
                                      </div>
                                      <p className="mb-0 text-dark">{ticketData?.asset?.id || t('shared.not_specified', 'Not specified')}</p>
                                    </div>

                                    <div className="info-item mb-0">
                                      <div className="d-flex align-items-center mb-1">
                                        <FeatherIcon icon="hash" size={14} className="text-muted me-2" />
                                        <span className="fw-semibold text-muted small">{t('shared.serial_number', 'Serial Number')}</span>
                                      </div>
                                      <p className="mb-0 text-dark">{ticketData?.asset?.serialNumber || t('shared.not_available', 'Not available')}</p>
                                    </div>
                                  </div>
                                </CardBody>
                              </Card>
                            </motion.div>
                          </Col>

                          {!isRequestor && (
                            <Col md={6}>
                              <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.3 }}>
                                <Card className="border-0 shadow-sm">
                                  <CardBody className="p-4">
                                    <div className="d-flex align-items-center mb-3">
                                      <div className="info-icon me-3">
                                        <div className="bg-info bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                             style={{ width: '40px', height: '40px' }}>
                                          <FeatherIcon icon="info" size={20} className="text-info" />
                                        </div>
                                      </div>
                                      <div>
                                        <h6 className="mb-1 fw-bold">{t('business.components.ticket_details_modal.sections.other_details', 'Other Details')}</h6>
                                        <p className="text-muted small mb-0">{t('business.components.ticket_details_modal.sections.other_details_sub', 'Location, vendor and category information')}</p>
                                      </div>
                                    </div>

                                    <div className="info-grid">
                                      <div className="info-item mb-3">
                                        <div className="d-flex align-items-center mb-1">
                                          <FeatherIcon icon="map-pin" size={14} className="text-muted me-2" />
                                          <span className="fw-semibold text-muted small">{t('shared.current_location', 'Current Location')}</span>
                                        </div>
                                        <p className="mb-0 text-dark">{ticketData?.asset?.currentLocation || ticketData?.locationName || t('shared.not_specified', 'Not specified')}</p>
                                      </div>

                                      <div className="info-item mb-3">
                                        <div className="d-flex align-items-center mb-1">
                                          <FeatherIcon icon="truck" size={14} className="text-muted me-2" />
                                          <span className="fw-semibold text-muted small">{t('shared.vendor', 'Vendor')}</span>
                                        </div>
                                        <div className="d-flex flex-wrap gap-1">
                                          {ticketData?.vendorNames?.length > 0 ? (
                                            ticketData.vendorNames.map((name, idx) => (
                                              <Badge key={idx} color="light" className="text-dark">
                                                {name}
                                              </Badge>
                                            ))
                                          ) : (
                                            <p className="mb-0 text-dark">{t('shared.not_specified', 'Not specified')}</p>
                                          )}
                                        </div>
                                      </div>

                                      <div className="info-item mb-0">
                                        <div className="d-flex align-items-center mb-1">
                                          <FeatherIcon icon="folder" size={14} className="text-muted me-2" />
                                          <span className="fw-semibold text-muted small">{t('shared.category', 'Category')}</span>
                                        </div>
                                        <div className="d-flex flex-wrap gap-1">
                                          {ticketData?.categoryNames?.length > 0 ? (
                                            ticketData.categoryNames.map((name, idx) => (
                                              <Badge key={idx} color="light" className="text-dark">
                                                {name}
                                              </Badge>
                                            ))
                                          ) : (
                                            <p className="mb-0 text-dark">{t('shared.not_specified', 'Not specified')}</p>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </CardBody>
                                </Card>
                              </motion.div>
                            </Col>
                          )}
                        </Row>

                        {/* Description */}
                        {ticketData?.description && (
                          <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.4 }} className="mb-4">
                            <Card className="border-0 shadow-sm">
                              <CardBody className="p-4">
                                <div className="d-flex align-items-center mb-3">
                                  <div className="info-icon me-3">
                                    <div className="bg-secondary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                         style={{ width: '40px', height: '40px' }}>
                                      <FeatherIcon icon="file-text" size={20} className="text-secondary" />
                                    </div>
                                  </div>
                                  <div>
                                    <h6 className="mb-1 fw-bold">{t('shared.description', 'Description')}</h6>
                                    <p className="text-muted small mb-0">{t('business.components.ticket_details_modal.sections.description_sub', 'Detailed problem description')}</p>
                                  </div>
                                </div>
                                <div className="bg-light rounded p-3">
                                  <p className="mb-0 text-dark">{ticketData.description}</p>
                                </div>
                              </CardBody>
                            </Card>
                          </motion.div>
                        )}

                        {/* People Information */}
                        <Row className="g-4 mb-4">
                          <Col md={6}>
                            <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.5 }}>
                              <Card className="border-0 shadow-sm h-100">
                                <CardBody className="p-4 text-center">
                                  <div className="mb-3">
                                    <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mx-auto"
                                         style={{ width: '48px', height: '48px' }}>
                                      <FeatherIcon icon="user" size={24} className="text-primary" />
                                    </div>
                                  </div>
                                  <h6 className="mb-2 fw-bold">{t('shared.requester', 'Requester')}</h6>
                                  <p className="mb-1 text-dark">{ticketData?.reportedBy?.name || t('shared.not_available', 'Not available')}</p>
                                  <Badge color="light" className="text-muted">
                                    {formatRoleName(ticketData?.reportedBy?.role, t) || t('shared.role_not_specified', 'Role not specified')}
                                  </Badge>
                                </CardBody>
                              </Card>
                            </motion.div>
                          </Col>

                          <Col md={6}>
                            <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.6 }}>
                              <Card className="border-0 shadow-sm h-100">
                                <CardBody className="p-4 text-center">
                                  <div className="mb-3">
                                    <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mx-auto"
                                         style={{ width: '48px', height: '48px' }}>
                                      <FeatherIcon icon="user-check" size={24} className="text-success" />
                                    </div>
                                  </div>
                                  <h6 className="mb-2 fw-bold">{t('shared.assigned_technicians', 'Assigned Technicians')}</h6>
                                  {(() => {
                                    const techList = Array.isArray(ticketData?.assignedTechnician)
                                      ? ticketData.assignedTechnician
                                      : ticketData?.assignedTechnician
                                        ? [ticketData.assignedTechnician]
                                        : [];
                                    const teamList = Array.isArray(ticketData?.assignedTeams)
                                      ? ticketData.assignedTeams
                                      : [];
                                    if (techList.length === 0 && teamList.length === 0) {
                                      return <p className="mb-1 text-muted">{t('shared.not_assigned', 'Not assigned')}</p>;
                                    }
                                    return (
                                      <div className="d-flex flex-wrap gap-2 justify-content-center">
                                        {techList.map((technician, index) => (
                                          <div key={`tech-${technician.id || index}`} className="d-flex align-items-center bg-light rounded p-2">
                                            <FeatherIcon icon="user" size={16} className="text-primary me-2" />
                                            <div>
                                              <div className="fw-medium text-dark small">{technician.name}</div>
                                              <small className="text-muted">{formatRoleName(technician.role, t) || t('shared.n_a', 'N/A')}</small>
                                            </div>
                                          </div>
                                        ))}
                                        {teamList.map((team, index) => (
                                          <div key={`team-${team.id || index}`} className="d-flex align-items-center rounded p-2"
                                               style={{ backgroundColor: `${team.color || '#0E7C7B'}1A` }}
                                               title={t('teams.assignedTeamTooltip', 'Team — {{count}} member(s) will be notified', { count: team.memberCount ?? 0 })}>
                                            <FeatherIcon icon="users" size={16} className="me-2"
                                                         style={{ color: team.color || '#0E7C7B' }} />
                                            <div>
                                              <div className="fw-medium text-dark small">{team.name}</div>
                                              {team.memberCount != null && (
                                                <small className="text-muted">
                                                  {t('teams.memberCount', '{{count}} member(s)', { count: team.memberCount })}
                                                </small>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    );
                                  })()}
                                </CardBody>
                              </Card>
                            </motion.div>
                          </Col>
                        </Row>
                      </div>

                    </TabPane>

                    {/* Occurrences Tab — for preventive recurring WOs */}
                    {isPreventiveRecurring && (
                      <TabPane tabId="occurrences">
                        <div className="p-4">
                          {/* Schedule Conditions Header */}
                          <Card className="border-0 shadow-sm border-start border-warning border-3 mb-4">
                            <CardBody className="p-4">
                              <div className="d-flex align-items-center mb-3">
                                <FeatherIcon icon="repeat" size={18} className="text-warning me-2" />
                                <h6 className="mb-0 fw-bold">{t('business.components.ticket_details_modal.schedule_conditions', 'Schedule Conditions')}</h6>
                              </div>

                              <p className="text-muted mb-2">
                                {
                                  {DAILY: t('business.components.ticket_details_modal.repeats_daily', 'Repeats daily'), WEEKLY: t('business.components.ticket_details_modal.repeats_weekly', 'Repeats weekly'), MONTHLY: t('business.components.ticket_details_modal.repeats_monthly', 'Repeats monthly'), YEARLY: t('business.components.ticket_details_modal.repeats_yearly', 'Repeats yearly')}[ticketData?.recurrenceType?.toUpperCase()] || `${t('business.components.ticket_details_modal.repeats_description', 'Repeats')} ${ticketData?.recurrenceType?.toLowerCase()}`
                                }
                                {ticketData?.recurrenceInterval > 1 && ` ${t('shared.every', 'every')} ${ticketData.recurrenceInterval}`}
                                {ticketData?.recurrenceNumber && (
                                  <span className="ms-2">
                                    <Badge color="primary" pill>
                                      #{ticketData.recurrenceNumber}
                                    </Badge>
                                  </span>
                                )}
                              </p>

                              {ticketData?.scheduledDate && (
                                <p className="text-muted mb-2">
                                  <FeatherIcon icon="calendar" size={14} className="me-1" />
                                  {t('business.components.ticket_details_modal.scheduled_date', 'Scheduled date')}: {formatCreatedDate(ticketData.scheduledDate)}
                                </p>
                              )}

                              {ticketData?.createdFromId && (
                                <p className="text-muted mb-2">
                                  <FeatherIcon icon="link" size={14} className="me-1" />
                                  {t('business.components.ticket_details_modal.auto_created_from', 'Automatically created from previous occurrence')}
                                </p>
                              )}

                              {isCompletedOccurrence && (
                                <Alert color="info" className="mb-2 py-2">
                                  <small>
                                    <FeatherIcon icon="info" size={14} className="me-1" />
                                    {t('business.components.ticket_details_modal.recurrence_read_only', 'To change the schedule, go to the most recent Work Order in the chain.')}
                                  </small>
                                </Alert>
                              )}
                            </CardBody>
                          </Card>

                          {/* Chain History Table */}
                          {loadingChainHistory ? (
                            <div className="text-center py-5">
                              <Spinner color="primary" />
                              <p className="text-muted mt-2 small">{t('shared.loading', 'Loading...')}</p>
                            </div>
                          ) : chainHistory.length > 0 ? (
                            <div className="table-responsive">
                              <Table hover className="mb-0">
                                <thead className="table-light">
                                  <tr>
                                    <th>#</th>
                                    <th>{t('shared.ticket_number', 'Ticket Number')}</th>
                                    <th>{t('shared.title', 'Title')}</th>
                                    <th>{t('shared.asset', 'Asset')}</th>
                                    <th>{t('shared.location', 'Location')}</th>
                                    <th>{t('shared.due_date', 'Due Date')}</th>
                                    <th>{t('shared.status', 'Status')}</th>
                                    <th>{t('shared.assigned_to', 'Assigned To')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {chainHistory.map((occ) => (
                                    <tr
                                      key={occ.id}
                                      className={occ.ticketNumber === ticketData?.ticketNumber ? 'table-active' : ''}
                                    >
                                      <td>{occ.recurrenceNumber || '-'}</td>
                                      <td>
                                        <span
                                          className="fw-medium text-primary cursor-pointer hover-underline"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (occ.ticketNumber && occ.ticketNumber !== ticketData?.ticketNumber) {
                                              (async () => {
                                                try {
                                                  const endpointPrefix = getDataEndpointPrefix(workType);
                                                  const { data } = await api.get(`${endpointPrefix}/get-by-id`, {
                                                    params: { id: occ.id }
                                                  });
                                                  setTicketData(data.response);
                                                  setActiveTab('details');
                                                } catch (error) {
                                                  console.error('Error loading occurrence:', error);
                                                }
                                              })();
                                            }
                                          }}
                                          style={{ cursor: 'pointer' }}
                                        >
                                          {occ.ticketNumber}
                                        </span>
                                      </td>
                                      <td>
                                        <span className="text-truncate d-inline-block" style={{ maxWidth: '200px' }} title={occ.title || ''}>
                                          {occ.title || '-'}
                                        </span>
                                      </td>
                                      <td>
                                        {occ.asset?.assetName ? (
                                          <span
                                            className={onAssetClick ? 'text-primary cursor-pointer hover-underline' : ''}
                                            onClick={onAssetClick ? (e) => {
                                              e.stopPropagation();
                                              onAssetClick(occ.asset?.id, occ.asset);
                                            } : undefined}
                                            style={{ cursor: onAssetClick ? 'pointer' : 'default' }}
                                          >
                                            {occ.asset.assetName}
                                          </span>
                                        ) : '-'}
                                      </td>
                                      <td>{occ.asset?.currentLocation || occ.locationName || '-'}</td>
                                      <td>{occ.requestDate ? formatCreatedDate(occ.requestDate) : '-'}</td>
                                      <td><WorkOrderStatusBadge status={occ.status} /></td>
                                      <td>{occ.assignedTechnician?.name || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          ) : (
                            <div className="text-center py-5">
                              <FeatherIcon icon="inbox" size={28} className="text-muted mb-2" />
                              <p className="text-muted mb-0">{t('business.components.ticket_details_modal.no_chain_history', 'No occurrence history found')}</p>
                            </div>
                          )}
                        </div>
                      </TabPane>
                    )}

                    {/* Management Tab */}
                    <TabPane tabId="management">
                      <div className="p-4">
                        {/* Status Management */}
                        <Card className="border-0 shadow-sm mb-4">
                          <CardBody className="p-4">
                            <div className="d-flex align-items-center mb-4">
                              <div className="info-icon me-3">
                                <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                     style={{ width: '40px', height: '40px' }}>
                                  <FeatherIcon icon="settings" size={20} className="text-primary" />
                                </div>
                              </div>
                              <div>
                                <h5 className="mb-1 fw-bold">{t('business.components.ticket_details_modal.sections.status_management', 'Status Management')}</h5>
                                <p className="text-muted small mb-0">{t('business.components.ticket_details_modal.sections.status_management_sub', 'Update ticket status and workflow')}</p>
                              </div>
                            </div>

                            <div className="status-buttons-grid">
                              <Row className="g-3">
                                {reactiveStatusOptions(t).map((item) => {
                                  const isActive = selectedStatus === item.value;
                                  return (
                                    <Col md={6} lg={4} key={item.value}>
                                      <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        className={`status-option-btn w-100 d-flex align-items-center p-3 rounded-3 border transition ${
                                          isActive ? 'border-primary bg-primary text-white shadow-sm' : 'border-light bg-white text-dark hover-shadow'
                                        }`}
                                        style={{
                                          borderWidth: '2px',
                                          cursor: 'pointer',
                                          transition: 'all 0.3s ease'
                                        }}
                                        onClick={() => handleStatusClick(item.value)}
                                      >
                                        <div className={`status-icon me-3 rounded-circle d-flex align-items-center justify-content-center ${
                                          isActive ? 'bg-white bg-opacity-10' : 'bg-light'
                                        }`} style={{ width: '40px', height: '40px' }}>
                                          <FeatherIcon 
                                            icon={item.icon} 
                                            size={18} 
                                            className={isActive ? 'text-white' : 'text-muted'} 
                                          />
                                        </div>
                                        <div className="text-start">
                                          <div className="fw-semibold">{item.label}</div>
                                          <small className={`${isActive ? 'text-white-50' : 'text-muted'}`}>
                                            {item.value === 'SUBMITTED' ? t('business.components.ticket_details_modal.status_descriptions.submitted', 'New ticket') :
                                             item.value === 'IN_PROGRESS' ? t('business.components.ticket_details_modal.status_descriptions.in_progress', 'Work in progress') :
                                             item.value === 'RESOLVED' ? t('business.components.ticket_details_modal.status_descriptions.resolved', 'Issue resolved') :
                                             item.value === 'ON_HOLD' ? t('business.components.ticket_details_modal.status_descriptions.on_hold', 'Temporarily paused') :
                                             item.value === 'PENDING' ? t('business.components.ticket_details_modal.status_descriptions.pending', 'Technician Assigned') : t('business.components.ticket_details_modal.status_descriptions.default', 'Status update')}
                                          </small>
                                        </div>
                                        {isActive && (
                                          <div className="ms-auto">
                                            <FeatherIcon icon="check" size={18} className="text-white" />
                                          </div>
                                        )}
                                      </motion.button>
                                    </Col>
                                  );
                                })}
                              </Row>
                            </div>
                          </CardBody>
                        </Card>

                        {/* Asset Status Card */}
                        {ticketData?.asset && (
                          <Card className="border-0 shadow-sm">
                            <CardBody className="p-4">
                              <div className="d-flex align-items-center mb-4">
                                <div className="info-icon me-3">
                                  <div className="bg-info bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                       style={{ width: '40px', height: '40px' }}>
                                    <FeatherIcon icon="cpu" size={20} className="text-info" />
                                  </div>
                                </div>
                                <div>
                                  <h5 className="mb-1 fw-bold">{t("shared.asset_status", "Asset Status")}</h5>
                                  <p className="text-muted small mb-0">{t('business.components.ticket_details_modal.sections.update_asset_status',"Update the linked asset's operational status")}</p>
                                </div>
                              </div>

                              <div className="d-flex align-items-center justify-content-between p-3 bg-light rounded">
                                <div>
                                  <div className="fw-medium text-dark mb-1">{ticketData.asset.assetName}</div>
                                  <div className="d-flex align-items-center gap-3 mb-2">
                                    {ticketData.asset.assetNumber && (
                                      <span className="text-muted small">Asset ID: {ticketData.asset.assetNumber}</span>
                                    )}
                                    {ticketData.asset.serialNumber && (
                                      <span className="text-muted small">Serial Number: {ticketData.asset.serialNumber}</span>
                                    )}
                                  </div>
                                  <div className="d-flex align-items-center">
                                    <span
                                      className="d-inline-block rounded-circle me-2"
                                      style={{
                                        width: '10px',
                                        height: '10px',
                                        backgroundColor: ticketData.asset.assetStatus === 'ONLINE' ? '#10b981'
                                          : ticketData.asset.assetStatus === 'OFFLINE' ? '#ef4444'
                                          : '#6b7280'
                                      }}
                                    />
                                    <Badge
                                      color={ticketData.asset.assetStatus === 'ONLINE' ? 'success'
                                        : ticketData.asset.assetStatus === 'OFFLINE' ? 'danger'
                                        : 'secondary'}
                                      className="px-2 py-1"
                                    >
                                      {ticketData.asset.assetStatus ? t(`shared.${ticketData.asset.assetStatus.toLowerCase()}`) : t('shared.unknown', 'Unknown')}
                                    </Badge>
                                  </div>
                                </div>
                                {ticketData.asset.record === false ? (
                                  <Badge color="warning" className="px-2 py-1">
                                    Decommissioned
                                  </Badge>
                                ) : hasPermission('asset:update') && (
                                  <Button
                                    color="primary"
                                    outline
                                    size="sm"
                                    onClick={() => setShowAssetStatusChangeModal(true)}
                                  >
                                    <FeatherIcon icon="refresh-cw" size={14} className="me-1" />
                                    {t('shared.buttons_change_status', 'Change Status')}
                                  </Button>
                                )}
                              </div>
                            </CardBody>
                          </Card>
                        )}

                        {/* Technician Assignment */}
                        <Card className="border-0 shadow-sm">
                          <CardBody className="p-4">
                            <div className="d-flex align-items-center mb-4">
                              <div className="info-icon me-3">
                                <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                     style={{ width: '40px', height: '40px' }}>
                                  <FeatherIcon icon="user-plus" size={20} className="text-success" />
                                </div>
                              </div>
                              <div className="flex-grow-1">
                                <h5 className="mb-1 fw-bold">{t('business.components.ticket_details_modal.sections.technician_assignment', 'Technician Assignment')}</h5>
                                <p className="text-muted small mb-0">{t('business.components.ticket_details_modal.sections.technician_assignment_sub', 'Assign or change the technician for this ticket')}</p>
                              </div>
                              <Button
                                color="primary"
                                className="d-flex align-items-center gap-2"
                                onClick={() => setShowAssignTechnicianModal(true)}
                              >
                                <FeatherIcon icon="users" size={16} />
                                {(
                                  (Array.isArray(ticketData?.assignedTechnician) && ticketData.assignedTechnician.length > 0) ||
                                  (ticketData?.assignedTechnician && !Array.isArray(ticketData.assignedTechnician)) ||
                                  (Array.isArray(ticketData?.assignedTeams) && ticketData.assignedTeams.length > 0)
                                )
                                  ? t('shared.buttons_manage_technicians', 'Manage Technicians')
                                  : t('shared.buttons_assign_technicians', 'Assign Technicians')}
                              </Button>
                            </div>

                            {/* Current Assignment Status */}
                            <div className="assignment-status bg-light rounded p-3">
                              <Row>
                                <Col md={12}>
                                  <div className="mb-2">
                                    <span className="fw-semibold text-muted small">{t('business.components.ticket_details_modal.labels.current_assignment', 'Current Assignment:')}</span>
                                    <div className="mt-2">
                                      {(() => {
                                        const techList = Array.isArray(ticketData?.assignedTechnician)
                                          ? ticketData.assignedTechnician
                                          : ticketData?.assignedTechnician
                                            ? [ticketData.assignedTechnician]
                                            : [];
                                        const teamList = Array.isArray(ticketData?.assignedTeams)
                                          ? ticketData.assignedTeams
                                          : [];
                                        if (techList.length === 0 && teamList.length === 0) {
                                          return (
                                            <div className="d-flex align-items-center text-muted">
                                              <div className="bg-secondary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-2"
                                                   style={{ width: '32px', height: '32px' }}>
                                                <FeatherIcon icon="user-x" size={16} className="text-secondary" />
                                              </div>
                                              <span>{t('business.components.ticket_details_modal.messages.no_technicians_assigned', 'No technicians assigned')}</span>
                                            </div>
                                          );
                                        }
                                        return (
                                          <div className="d-flex flex-wrap gap-2">
                                            {techList.map((technician, index) => (
                                              <div key={`tech-${technician.id || index}`} className="d-flex align-items-center bg-success bg-opacity-10 rounded p-2">
                                                <div className="bg-success bg-opacity-20 rounded-circle d-flex align-items-center justify-content-center me-2"
                                                     style={{ width: '24px', height: '24px' }}>
                                                  <FeatherIcon icon="user-check" size={12} className="text-success" />
                                                </div>
                                                <div>
                                                  <div className="fw-medium text-dark small">{technician.name}</div>
                                                  <small className="text-muted">{formatRoleName(technician.role, t)}</small>
                                                </div>
                                              </div>
                                            ))}
                                            {teamList.map((team, index) => (
                                              <div key={`team-${team.id || index}`} className="d-flex align-items-center rounded p-2"
                                                   style={{ backgroundColor: `${team.color || '#0E7C7B'}1A` }}
                                                   title={t('teams.assignedTeamTooltip', 'Team — {{count}} member(s) will be notified', { count: team.memberCount ?? 0 })}>
                                                <div className="rounded-circle d-flex align-items-center justify-content-center me-2"
                                                     style={{ width: '24px', height: '24px', backgroundColor: team.color || '#0E7C7B' }}>
                                                  <FeatherIcon icon="users" size={12} className="text-white" />
                                                </div>
                                                <div>
                                                  <div className="fw-medium text-dark small">{team.name}</div>
                                                  {team.memberCount != null && (
                                                    <small className="text-muted">
                                                      {t('teams.memberCount', '{{count}} member(s)', { count: team.memberCount })}
                                                    </small>
                                                  )}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  </div>
                                </Col>
                              </Row>
                            </div>
                          </CardBody>
                        </Card>
                      </div>
                    </TabPane>

                    {/* Activity Tab */}
                    <TabPane tabId="activity">
                      <div className="p-4">
                        <Card className="border-0 shadow-sm">
                          <CardBody className="p-4">
                            <div className="d-flex align-items-center mb-4">
                              <div className="info-icon me-3">
                                <div className="bg-info bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                     style={{ width: '40px', height: '40px' }}>
                                  <FeatherIcon icon="activity" size={20} className="text-info" />
                                </div>
                              </div>
                              <div>
                                <h5 className="mb-1 fw-bold">{t('shared.activity_timeline', 'Activity Timeline')}</h5>
                                <p className="text-muted small mb-0">{t('business.components.ticket_details_modal.sections.activity_timeline_sub', 'Complete history of ticket activities')}</p>
                              </div>
                            </div>

                            {loadingActivities ? (
                              <div className="text-center py-5">
                                <Spinner color="primary" size="lg" />
                                <p className="text-muted mt-3">{t('shared.loading_activity_log', 'Loading activity log...')}</p>
                              </div>
                            ) : activities.length > 0 ? (
                              <div className="activity-timeline" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                {activities.map((activity, index) => (
                                  <motion.div
                                    key={activity.id}
                                    className="timeline-item d-flex mb-4"
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                  >
                                    <div className="timeline-marker me-3">
                                      <div className="timeline-dot bg-primary rounded-circle d-flex align-items-center justify-content-center"
                                           style={{ width: '32px', height: '32px', minWidth: '32px' }}>
                                        <FeatherIcon icon="clock" size={14} className="text-white" />
                                      </div>
                                    </div>
                                    <div className="timeline-content flex-grow-1">
                                      <div className="timeline-card bg-light rounded p-3">
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                          <div>
                                            <div className="fw-semibold text-dark">{activity.userName}</div>
                                            <div className="text-muted">{translateActivity(activity, t)}</div>
                                          </div>
                                          <Badge color="light" className="text-muted small">
                                            <FeatherIcon icon="calendar" size={12} className="me-1" />
                                            {formatCreatedDate(activity?.createdAt)}
                                            {activity?.createdAt && (
                                              <span className="ms-1">
                                                {new Date(activity.createdAt).toLocaleTimeString(navigator.language || 'en-IN', { hour: '2-digit', minute: '2-digit' })}
                                              </span>
                                            )}
                                          </Badge>
                                        </div>
                                      </div>
                                    </div>
                                  </motion.div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center py-5">
                                <div className="empty-state-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                                     style={{ width: '64px', height: '64px', backgroundColor: '#f8f9fa' }}>
                                  <FeatherIcon icon="clock" size={24} className="text-muted" />
                                </div>
                                <h5 className="text-muted">{t('shared.no_activity_recorded', 'No activity recorded')}</h5>
                                <p className="text-muted mb-0">{t('shared.activity_will_appear_here', 'Activity will appear here as the ticket progresses')}</p>
                              </div>
                            )}

                            {/* Comment Section */}
                            <div className="comment-section mt-4 pt-4 border-top">
                              <div className="d-flex align-items-center mb-3">
                                <div className="info-icon me-3">
                                  <div className="bg-secondary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                       style={{ width: '32px', height: '32px' }}>
                                    <FeatherIcon icon="message-circle" size={16} className="text-secondary" />
                                  </div>
                                </div>
                                <h6 className="mb-0 fw-bold">{t('shared.add_comment', 'Add Comment')}</h6>
                              </div>

                              <Form onSubmit={handleCommentSubmit}>
                                <div className="d-flex gap-3 align-items-start">
                                  <div className="comment-input flex-grow-1">
                                    <Input
                                      type="textarea"
                                      placeholder={t('business.components.ticket_details_modal.placeholders.add_comment', 'Add a comment to this ticket...')}
                                      value={commentText}
                                      onChange={(e) => setCommentText(e.target.value)}
                                      onKeyPress={handleCommentKeyPress}
                                      rows={3}
                                      className="border-0 shadow-sm"
                                      style={{
                                        resize: 'none',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '12px',
                                        padding: '12px 16px'
                                      }}
                                      disabled={submittingComment}
                                    />
                                  </div>
                                  <div className="comment-actions d-flex flex-column gap-2">
                                    <Button
                                      type="submit"
                                      color="primary"
                                      size="sm"
                                      className="d-flex align-items-center gap-2"
                                      disabled={!commentText.trim() || submittingComment}
                                      style={{ minWidth: '100px' }}
                                    >
                                      {submittingComment ? (
                                        <>
                                          <Spinner size="sm" />
                                          {t('shared.posting', 'Posting...')}
                                        </>
                                      ) : (
                                        <>
                                          <FeatherIcon icon="send" size={14} />
                                          {t('shared.post', 'Post')}
                                        </>
                                      )}
                                    </Button>
                                    {commentText.trim() && (
                                      <Button
                                        type="button"
                                        color="light"
                                        size="sm"
                                        onClick={() => setCommentText('')}
                                        disabled={submittingComment}
                                      >
                                        <FeatherIcon icon="x" size={14} />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                                <div className="comment-help mt-2">
                                  <small className="text-muted d-flex align-items-center">
                                    <FeatherIcon icon="info" size={12} className="me-1" />
                                    {t('shared.press_enter_to_submit_or_shift_enter_for', 'Press Enter to submit or Shift+Enter for new line')}
                                  </small>
                                </div>
                              </Form>
                            </div>
                          </CardBody>
                        </Card>
                      </div>
                    </TabPane>

                    {/* Spare Parts Tab */}
                    <TabPane tabId="spareParts">
                      <div className="p-4">
                        <Card className="border-0 shadow-sm">
                          <CardBody className="p-4">
                            <div className="d-flex align-items-center justify-content-between mb-4">
                              <div className="d-flex align-items-center">
                                <div className="info-icon me-3">
                                  <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                       style={{ width: '40px', height: '40px' }}>
                                    <FeatherIcon icon="tool" size={20} className="text-primary" />
                                  </div>
                                </div>
                                <div>
                                  <h5 className="mb-1 fw-bold">{t('shared.spare_parts_usage', 'Spare Parts Usage')}</h5>
                                  <p className="text-muted small mb-0">{t('business.components.ticket_details_modal.sections.spare_parts_usage_sub', 'Spare parts used for this ticket')}</p>
                                </div>
                              </div>
                              <Button
                                color="success"
                                size="sm"
                                className="d-flex align-items-center gap-2"
                                onClick={() => {
                                  fetchAvailableSpareParts();
                                  setAddSparePartModal(true);
                                  setAddSparePartError('');
                                }}
                              >
                                <FeatherIcon icon="plus" size={16} />
                                {t('business.spare_parts.add_parts', 'Add Parts')}
                              </Button>
                            </div>

                            {loadingSparePartsConsumables ? (
                              <div className="text-center py-5">
                                <Spinner color="primary" size="lg" />
                                <p className="text-muted mt-3">{t('shared.loading_spare_parts', 'Loading spare parts...')}</p>
                              </div>
                            ) : sparePartsUsage.length > 0 ? (
                              <div className="table-responsive">
                                <Table hover className="mb-0">
                                  <thead className="bg-light">
                                    <tr>
                                      <th>#</th>
                                      <th>{t('shared.spare_part_name', 'Spare Part Name')}</th>
                                      <th>{t('shared.quantity', 'Quantity')}</th>
                                      <th>{t('shared.date', 'Date')}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <AnimatePresence>
                                      {sparePartsUsage.map((sparePart, index) => (
                                        <motion.tr
                                          key={sparePart.id}
                                          initial={{ opacity: 0, y: 10 }}
                                          animate={{ opacity: 1, y: 0 }}
                                          exit={{ opacity: 0, y: -10 }}
                                          transition={{ duration: 0.2, delay: index * 0.05 }}
                                        >
                                          <td>{index + 1}</td>
                                          <td className="fw-medium">
                                            {sparePart.sparePartName || 'N/A'}
                                          </td>
                                          <td>
                                            <Badge color="info" pill>{sparePart.quantity}</Badge>
                                          </td>
                                          <td className="text-muted">{sparePart.createdAt ? formatCreatedDate(sparePart.createdAt) : '-'}</td>
                                        </motion.tr>
                                      ))}
                                    </AnimatePresence>
                                  </tbody>
                                </Table>
                              </div>
                            ) : (
                              <div className="text-center py-5">
                                <div className="empty-state-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                                     style={{ width: '64px', height: '64px', backgroundColor: '#f8f9fa' }}>
                                  <FeatherIcon icon="tool" size={24} className="text-muted" />
                                </div>
                                <h5 className="text-muted">{t('shared.no_spare_parts_usage', 'No spare parts usage')}</h5>
                                <p className="text-muted mb-0">{t('business.components.ticket_details_modal.messages.no_spare_parts_usage_sub', 'No spare parts have been used for this ticket')}</p>
                              </div>
                            )}
                          </CardBody>
                        </Card>
                      </div>

                      {/* Add Spare Part Modal */}
                      <Modal isOpen={addSparePartModal} toggle={() => setAddSparePartModal(false)} size="md" centered>
                        <div className="modal-header">
                          <h5 className="modal-title">{t('business.spare_parts.add_parts', 'Add Parts')}</h5>
                          <button type="button" className="btn-close" onClick={() => setAddSparePartModal(false)} />
                        </div>
                        <div className="modal-body">
                          {addSparePartError && (
                            <Alert color="danger" className="mb-3">{addSparePartError}</Alert>
                          )}
                          <div className="mb-4">
                            <label className="form-label fw-medium">
                              {t('business.spare_parts.spare_part_singular', 'Spare Part')} <span className="text-danger">*</span>
                            </label>
                            <Select
                              options={availableSpareParts.map(sp => ({
                                value: sp.id,
                                label: `${sp.name}${sp.quantity != null ? ` (${sp.quantity} ${t('business.spare_parts.in_stock', 'in stock')})` : ''}`
                              }))}
                              value={selectedSparePart}
                              onChange={setSelectedSparePart}
                              isClearable
                              isSearchable
                              placeholder={t('business.spare_parts.select_spare_part', 'Select Spare Part')}
                              classNamePrefix="react-select"
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label fw-medium">
                              {t('shared.quantity', 'Quantity')} <span className="text-danger">*</span>
                            </label>
                            <Input
                              type="number"
                              min="1"
                              value={sparePartQuantity}
                              onChange={(e) => setSparePartQuantity(e.target.value)}
                              placeholder={t('business.spare_parts.enter_quantity', 'Enter quantity')}
                            />
                          </div>
                        </div>
                        <div className="modal-footer">
                          <Button color="secondary" onClick={() => setAddSparePartModal(false)}>
                            {t('shared.cancel', 'Cancel')}
                          </Button>
                          <Button color="primary" onClick={handleAddSparePart} disabled={addingSparePartLoading}>
                            {addingSparePartLoading ? (
                              <><Spinner size="sm" className="me-2" />{t('shared.adding', 'Adding...')}</>
                            ) : (
                              <><FeatherIcon icon="plus" size={16} className="me-1" />{t('business.spare_parts.add_parts', 'Add Parts')}</>
                            )}
                          </Button>
                        </div>
                      </Modal>
                    </TabPane>

                    {/* Consumables Tab */}
                    <TabPane tabId="consumables">
                      <div className="p-4">
                        <Card className="border-0 shadow-sm">
                          <CardBody className="p-4">
                            <div className="d-flex align-items-center mb-4">
                              <div className="info-icon me-3">
                                <div className="bg-secondary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                     style={{ width: '40px', height: '40px' }}>
                                  <FeatherIcon icon="package" size={20} className="text-secondary" />
                                </div>
                              </div>
                              <div>
                                <h5 className="mb-1 fw-bold">{t('shared.consumables_usage', 'Consumables Usage')}</h5>
                                <p className="text-muted small mb-0">{t('business.components.ticket_details_modal.sections.consumables_usage_sub', 'Consumables used for this ticket')}</p>
                              </div>
                            </div>

                            {loadingSparePartsConsumables ? (
                              <div className="text-center py-5">
                                <Spinner color="primary" size="lg" />
                                <p className="text-muted mt-3">{t('shared.loading_consumables', 'Loading consumables...')}</p>
                              </div>
                            ) : consumablesUsage.length > 0 ? (
                              <div className="table-responsive">
                                <Table hover className="mb-0">
                                  <thead className="bg-light">
                                    <tr>
                                      <th>#</th>
                                      <th>{t('shared.consumable_name', 'Consumable Name')}</th>
                                      <th>{t('shared.quantity', 'Quantity')}</th>
                                      <th>{t('shared.status', 'Status')}</th>
                                      <th>{t('shared.date', 'Date')}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <AnimatePresence>
                                      {consumablesUsage.map((consumable, index) => (
                                        <motion.tr
                                          key={consumable.id}
                                          initial={{ opacity: 0, y: 10 }}
                                          animate={{ opacity: 1, y: 0 }}
                                          exit={{ opacity: 0, y: -10 }}
                                          transition={{ duration: 0.2, delay: index * 0.05 }}
                                        >
                                          <td>{index + 1}</td>
                                          <td className="fw-medium">
                                            {consumable.consumablesResponse?.name || 'N/A'}
                                          </td>
                                          <td>
                                            <Badge color="info" pill>{consumable.quantity}</Badge>
                                          </td>
                                          <td>
                                            <Badge color="success" pill>
                                              {t('shared.used', 'Used')}
                                            </Badge>
                                          </td>
                                          <td className="text-muted">{consumable.createdAt ? formatCreatedDate(consumable.createdAt) : '-'}</td>
                                        </motion.tr>
                                      ))}
                                    </AnimatePresence>
                                  </tbody>
                                </Table>
                              </div>
                            ) : (
                              <div className="text-center py-5">
                                <div className="empty-state-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                                     style={{ width: '64px', height: '64px', backgroundColor: '#f8f9fa' }}>
                                  <FeatherIcon icon="package" size={24} className="text-muted" />
                                </div>
                                <h5 className="text-muted">{t('shared.no_consumables_usage', 'No consumables usage')}</h5>
                                <p className="text-muted mb-0">{t('business.components.ticket_details_modal.messages.no_consumables_usage_sub', 'No consumables have been used for this ticket')}</p>
                              </div>
                            )}
                          </CardBody>
                        </Card>
                      </div>
                    </TabPane>

                    {/* Tools Tab */}
                    <TabPane tabId="tools">
                      <div className="p-4">
                        {/* Required Tools Section - commented out for now
                        <Card className="border-0 shadow-sm mb-4">
                          <CardBody className="p-4">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                              <div className="d-flex align-items-center">
                                <div className="info-icon me-3">
                                  <div className="bg-info bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                       style={{ width: '40px', height: '40px' }}>
                                    <FeatherIcon icon="list" size={20} className="text-info" />
                                  </div>
                                </div>
                                <div>
                                  <h5 className="mb-1 fw-bold">{t('business.tools.required_tools', 'Required Tools')}</h5>
                                  <p className="text-muted small mb-0">{t('business.tools.required_tools_desc', 'Tools needed for this work order')}</p>
                                </div>
                              </div>
                            </div>

                            {loadingRequiredTools ? (
                              <div className="text-center py-3">
                                <Spinner size="sm" color="primary" />
                              </div>
                            ) : (
                              <>
                                {requiredTools.length > 0 && (
                                  <div className="mb-3">
                                    <AnimatePresence>
                                      {requiredTools.map((rt, index) => (
                                        <motion.div
                                          key={rt.id}
                                          initial={{ opacity: 0, y: 10 }}
                                          animate={{ opacity: 1, y: 0 }}
                                          exit={{ opacity: 0, y: -10 }}
                                          transition={{ duration: 0.2, delay: index * 0.05 }}
                                          className="d-flex align-items-center justify-content-between bg-light rounded p-2 mb-2"
                                        >
                                          <div>
                                            <span className="fw-medium">{rt.toolName}</span>
                                            <Badge color="primary" className="ms-2" pill>{t('business.tools.qty', 'Qty')}: {rt.quantity}</Badge>
                                            {rt.notes && <small className="text-muted ms-2">{rt.notes}</small>}
                                          </div>
                                          <PermissionGuard permission="tool:delete">
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-outline-danger"
                                              onClick={() => handleDeleteRequiredTool(rt.id)}
                                            >
                                              <FeatherIcon icon="trash-2" size={14} />
                                            </button>
                                          </PermissionGuard>
                                        </motion.div>
                                      ))}
                                    </AnimatePresence>
                                  </div>
                                )}

                                {requiredTools.length === 0 && (
                                  <div className="text-center py-3 text-muted small mb-3">
                                    {t('business.tools.no_required_tools', 'No required tools added')}
                                  </div>
                                )}

                                <PermissionGuard permission="tool:create">
                                  <div className="border rounded p-3 bg-light">
                                    <div className="d-flex gap-2 align-items-end">
                                      <div className="flex-grow-1">
                                        <label className="form-label small">{t('shared.tool_name', 'Tool Name')}</label>
                                        <Input
                                          type="text"
                                          bsSize="sm"
                                          value={newRequiredTool.toolName}
                                          onChange={(e) => setNewRequiredTool({...newRequiredTool, toolName: e.target.value})}
                                          placeholder={t('business.tools.enter_tool_name', 'Enter tool name')}
                                        />
                                      </div>
                                      <div style={{ width: '80px' }}>
                                        <label className="form-label small">{t('business.tools.qty', 'Qty')}</label>
                                        <Input
                                          type="number"
                                          bsSize="sm"
                                          min="1"
                                          value={newRequiredTool.quantity}
                                          onChange={(e) => setNewRequiredTool({...newRequiredTool, quantity: e.target.value})}
                                        />
                                      </div>
                                      <div className="flex-grow-1">
                                        <label className="form-label small">{t('business.tools.notes', 'Notes')}</label>
                                        <Input
                                          type="text"
                                          bsSize="sm"
                                          value={newRequiredTool.notes}
                                          onChange={(e) => setNewRequiredTool({...newRequiredTool, notes: e.target.value})}
                                          placeholder={t('business.tools.optional_notes', 'Optional notes')}
                                        />
                                      </div>
                                      <Button
                                        color="primary"
                                        size="sm"
                                        onClick={handleAddRequiredTool}
                                        disabled={!newRequiredTool.toolName.trim()}
                                      >
                                        <FeatherIcon icon="plus" size={14} className="me-1" />
                                        {t('shared.add', 'Add')}
                                      </Button>
                                    </div>
                                  </div>
                                </PermissionGuard>
                              </>
                            )}
                          </CardBody>
                        </Card>
                        */}

                        {/* Checked Out Tools Section */}
                        <Card className="border-0 shadow-sm">
                          <CardBody className="p-4">
                            <div className="d-flex align-items-center justify-content-between mb-4">
                              <div className="d-flex align-items-center">
                                <div className="info-icon me-3">
                                  <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                       style={{ width: '40px', height: '40px' }}>
                                    <FeatherIcon icon="tool" size={20} className="text-primary" />
                                  </div>
                                </div>
                                <div>
                                  <h5 className="mb-1 fw-bold">{t('business.tools.checked_out_tools', 'Checked Out Tools')}</h5>
                                  <p className="text-muted small mb-0">{t('business.tools.checked_out_tools_desc', 'Tools currently in use for this work order')}</p>
                                </div>
                              </div>
                              <PermissionGuard permission="tool:update">
                                <Button
                                  color="success"
                                  size="sm"
                                  className="d-flex align-items-center gap-2"
                                  onClick={() => setToolCheckoutModal(true)}
                                >
                                  <FeatherIcon icon="plus" size={16} />
                                  {t('business.tools.check_out', 'Check Out')}
                                </Button>
                              </PermissionGuard>
                            </div>

                            {loadingCheckedOutTools ? (
                              <div className="text-center py-5">
                                <Spinner color="primary" size="lg" />
                                <p className="text-muted mt-3">{t('shared.loading', 'Loading...')}</p>
                              </div>
                            ) : checkedOutTools.length > 0 ? (
                              <div className="table-responsive">
                                <Table hover className="mb-0">
                                  <thead className="bg-light">
                                    <tr>
                                      <th>#</th>
                                      <th>{t('shared.tool_name', 'Tool Name')}</th>
                                      <th>{t('shared.user', 'User')}</th>
                                      <th>{t('shared.date', 'Date')}</th>
                                      <th>{t('shared.condition', 'Condition')}</th>
                                      <th>{t('shared.status', 'Status')}</th>
                                      <th>{t('shared.actions', 'Actions')}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <AnimatePresence>
                                      {checkedOutTools.map((ct, index) => (
                                        <motion.tr
                                          key={ct.id}
                                          initial={{ opacity: 0, y: 10 }}
                                          animate={{ opacity: 1, y: 0 }}
                                          exit={{ opacity: 0, y: -10 }}
                                          transition={{ duration: 0.2, delay: index * 0.05 }}
                                        >
                                          <td>{index + 1}</td>
                                          <td>
                                            <span className="fw-medium">{ct.tool?.name || t('shared.n_a', 'N/A')}</span>
                                            {ct.tool?.serialNumber && (
                                              <small className="d-block text-muted">{ct.tool.serialNumber}</small>
                                            )}
                                          </td>
                                          <td><small>{ct.checkedOutByUser?.name || ct.user?.name || ct.checkedOutByName || '-'}</small></td>
                                          <td className="text-muted">{formatToolDate(ct.checkedOutAt)}</td>
                                          <td>
                                            {ct.conditionAtCheckout && (
                                              <Badge color={
                                                ct.conditionAtCheckout === 'EXCELLENT' ? 'success' :
                                                ct.conditionAtCheckout === 'GOOD' ? 'primary' :
                                                ct.conditionAtCheckout === 'FAIR' ? 'warning' : 'danger'
                                              } pill>
                                                {t(`shared.condition_${ct.conditionAtCheckout.toLowerCase()}`, ct.conditionAtCheckout)}
                                              </Badge>
                                            )}
                                          </td>
                                          <td>
                                            <Badge color={ct.status === 'CHECKED_OUT' ? 'warning' : 'success'} pill>
                                              {ct.status === 'CHECKED_OUT' ? t('business.tools.status_checked_out', 'Checked Out') : t('business.tools.status_returned', 'Returned')}
                                            </Badge>
                                          </td>
                                          <td>
                                            {ct.status === 'CHECKED_OUT' && (
                                              <PermissionGuard permission="tool:update">
                                                <button
                                                  type="button"
                                                  className="btn btn-sm btn-outline-success"
                                                  onClick={() => handleToolCheckinFromWO(ct.id)}
                                                >
                                                  <FeatherIcon icon="log-in" size={14} className="me-1" />
                                                  {t('business.tools.check_in', 'Check In')}
                                                </button>
                                              </PermissionGuard>
                                            )}
                                          </td>
                                        </motion.tr>
                                      ))}
                                    </AnimatePresence>
                                  </tbody>
                                </Table>
                              </div>
                            ) : (
                              <div className="text-center py-5">
                                <div className="empty-state-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                                     style={{ width: '64px', height: '64px', backgroundColor: '#f8f9fa' }}>
                                  <FeatherIcon icon="tool" size={24} className="text-muted" />
                                </div>
                                <h5 className="text-muted">{t('business.tools.no_checked_out_tools', 'No tools checked out for this work order')}</h5>
                              </div>
                            )}
                          </CardBody>
                        </Card>
                      </div>

                    </TabPane>

                    {/* Media Tab */}
                    <TabPane tabId="attachments">
                      <div className="p-4">
                        <Card className="border-0 shadow-sm">
                          <CardBody className="p-4">
                            {/* Header with upload button */}
                            <div className="d-flex align-items-center justify-content-between mb-4">
                              <div className="d-flex align-items-center">
                                <div className="info-icon me-3">
                                  <div className="bg-info bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                       style={{ width: '40px', height: '40px' }}>
                                    <FeatherIcon icon="paperclip" size={20} className="text-info" />
                                  </div>
                                </div>
                                <div>
                                  <h5 className="mb-1 fw-bold">{t('shared.media', 'Media')}</h5>
                                  <p className="text-muted small mb-0">{t('shared.media_section_subtitle', 'Service reports, images, and documents')}</p>
                                </div>
                              </div>
                              <div>
                                <Button
                                  color="primary"
                                  onClick={() => attachmentInputRef.current?.click()}
                                  disabled={uploadingAttachments}
                                  className="d-flex align-items-center gap-2"
                                >
                                  {uploadingAttachments ? (
                                    <>
                                      <Spinner size="sm" />
                                      {t('shared.uploading', 'Uploading...')}
                                    </>
                                  ) : (
                                    <>
                                      <FeatherIcon icon="upload" size={16} />
                                      {t('shared.upload_files', 'Upload Files')}
                                    </>
                                  )}
                                </Button>
                                <input
                                  type="file"
                                  ref={attachmentInputRef}
                                  multiple
                                  hidden
                                  onChange={handleAttachmentUpload}
                                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                                />
                              </div>
                            </div>

                            {/* Validation Errors */}
                            {attachmentErrors.length > 0 && (
                              <div className="mb-4">
                                {attachmentErrors.map((error, index) => (
                                  <Alert
                                    key={index}
                                    color="danger"
                                    className="py-2 mb-2 d-flex align-items-center justify-content-between"
                                  >
                                    <div className="d-flex align-items-center">
                                      <FeatherIcon icon="alert-circle" size={16} className="me-2" />
                                      <small>{error}</small>
                                    </div>
                                    <button
                                      type="button"
                                      className="btn-close"
                                      style={{ fontSize: '0.7rem' }}
                                      onClick={() => dismissAttachmentError(index)}
                                    />
                                  </Alert>
                                ))}
                              </div>
                            )}

                            {/* Images Section */}
                            {ticketData?.imageUrls?.length > 0 && (
                              <div className="mb-4">
                                <h6 className="fw-semibold mb-3">
                                  <FeatherIcon icon="image" size={16} className="me-2" />
                                  {t('shared.images', 'Images')} ({ticketData.imageUrls.length})
                                </h6>
                                <Row className="g-3">
                                  {ticketData.imageUrls.map((url, index) => (
                                    <Col key={index} md={3} sm={4} xs={6}>
                                      <motion.div
                                        className="position-relative"
                                        whileHover={{ scale: 1.02 }}
                                        transition={{ duration: 0.2 }}
                                      >
                                        <img
                                          src={url}
                                          alt={`Attachment ${index + 1}`}
                                          className="img-fluid rounded shadow-sm"
                                          style={{
                                            width: '100%',
                                            height: '120px',
                                            objectFit: 'cover',
                                            cursor: 'pointer'
                                          }}
                                          onClick={() => window.open(url, '_blank')}
                                        />
                                        <div className="position-absolute top-0 end-0 p-1">
                                          <Button
                                            color="danger"
                                            size="sm"
                                            className="rounded-circle p-1"
                                            style={{ width: '28px', height: '28px' }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDeleteAttachment(url, 'image');
                                            }}
                                            disabled={deletingAttachment === url}
                                          >
                                            {deletingAttachment === url ? (
                                              <Spinner size="sm" style={{ width: '12px', height: '12px' }} />
                                            ) : (
                                              <FeatherIcon icon="trash-2" size={12} />
                                            )}
                                          </Button>
                                        </div>
                                        <div className="position-absolute bottom-0 start-0 end-0 p-1">
                                          <Button
                                            color="light"
                                            size="sm"
                                            className="w-100 opacity-75"
                                            onClick={() => window.open(url, '_blank')}
                                          >
                                            <FeatherIcon icon="download" size={12} className="me-1" />
                                            {t('shared.download', 'Download')}
                                          </Button>
                                        </div>
                                      </motion.div>
                                    </Col>
                                  ))}
                                </Row>
                              </div>
                            )}

                            {/* Documents Section */}
                            {ticketData?.documentUrls?.length > 0 && (
                              <div className="mb-4">
                                <h6 className="fw-semibold mb-3">
                                  <FeatherIcon icon="file-text" size={16} className="me-2" />
                                  {t('shared.documents', 'Documents')} ({ticketData.documentUrls.length})
                                </h6>
                                <div className="documents-list">
                                  {ticketData.documentUrls.map((url, index) => (
                                    <motion.div
                                      key={index}
                                      className="d-flex align-items-center p-3 bg-light rounded mb-2"
                                      whileHover={{ backgroundColor: '#e9ecef' }}
                                      transition={{ duration: 0.2 }}
                                    >
                                      <div
                                        className="bg-white rounded d-flex align-items-center justify-content-center me-3"
                                        style={{ width: '40px', height: '40px' }}
                                      >
                                        <FeatherIcon icon={getDocumentIcon(url)} size={20} className="text-primary" />
                                      </div>
                                      <div className="flex-grow-1 overflow-hidden">
                                        <p className="mb-0 fw-medium text-truncate">{getFileNameFromUrl(url)}</p>
                                        <small className="text-muted">
                                          {url.split('.').pop()?.toUpperCase()} {t('shared.document', 'Document')}
                                        </small>
                                      </div>
                                      <div className="d-flex gap-2">
                                        <Button
                                          color="outline-primary"
                                          size="sm"
                                          onClick={() => window.open(url, '_blank')}
                                        >
                                          <FeatherIcon icon="download" size={14} />
                                        </Button>
                                        <Button
                                          color="outline-danger"
                                          size="sm"
                                          onClick={() => handleDeleteAttachment(url, 'document')}
                                          disabled={deletingAttachment === url}
                                        >
                                          {deletingAttachment === url ? (
                                            <Spinner size="sm" style={{ width: '14px', height: '14px' }} />
                                          ) : (
                                            <FeatherIcon icon="trash-2" size={14} />
                                          )}
                                        </Button>
                                      </div>
                                    </motion.div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Empty State */}
                            {(!ticketData?.imageUrls?.length && !ticketData?.documentUrls?.length) && (
                              <div className="text-center py-5">
                                <div className="empty-state-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                                     style={{ width: '64px', height: '64px', backgroundColor: '#f8f9fa' }}>
                                  <FeatherIcon icon="paperclip" size={24} className="text-muted" />
                                </div>
                                <h5 className="text-muted">{t('shared.no_media', 'No media files')}</h5>
                                <p className="text-muted mb-3">{t('shared.upload_instructions', 'Upload images or documents using the button above')}</p>
                                <Button
                                  color="outline-primary"
                                  onClick={() => attachmentInputRef.current?.click()}
                                  disabled={uploadingAttachments}
                                >
                                  <FeatherIcon icon="upload" size={16} className="me-2" />
                                  {t('shared.upload_files', 'Upload Files')}
                                </Button>
                              </div>
                            )}
                          </CardBody>
                        </Card>
                      </div>
                    </TabPane>
                    <TabPane tabId="timeAndCost" className="h-100">
                      <div className="p-4">
                        {ticketData?.id ? (() => {
                          let currentUserId = null;
                          try {
                            const stored = localStorage.getItem('authUser');
                            currentUserId = stored ? JSON.parse(stored)?.id : null;
                          } catch (e) {
                            currentUserId = null;
                          }
                          return (
                            <>
                              <WoCostSection
                                workOrderId={ticketData.id}
                                workOrderStatus={ticketData?.status}
                                currentUserId={currentUserId}
                              />
                              <TimeEntrySection
                                workOrderId={ticketData.id}
                                workOrderStatus={ticketData?.status}
                                currentUserId={currentUserId}
                              />
                              <div className="mt-4">
                                <StatusTimeline workOrderId={ticketData.id} />
                              </div>
                            </>
                          );
                        })() : null}
                      </div>
                    </TabPane>
                  </TabContent>
                )}
              </div>
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>

      {/* Assign Technician Modal */}
      <AssignTechnicianModal
        isOpen={showAssignTechnicianModal}
        toggle={() => {
          setShowAssignTechnicianModal(false);
          setPendingStatusSelected(false);
        }}
        ticketId={ticketData?.id}
        assignedTechnician={ticketData?.assignedTechnician}
        assignedTeams={ticketData?.assignedTeams}
        onAssign={handleAssignTechnician}
        isPendingStatus={pendingStatusSelected}
      />

      {/* Service Report Upload Modal (shown when resolving) */}
      <ServiceReportUploadModal
        isOpen={showResolvedUploadModal}
        toggle={() => setShowResolvedUploadModal(false)}
        onSkip={handleResolveWithoutUpload}
        onUploadComplete={handleResolveWithUpload}
      />

      {/* Asset Status Change Modal — workOrderId stamps the resulting AssetStatusHistory
          row so its downtime cost contributes to this WO's total. */}
      <AssetStatusChangeModal
        isOpen={showAssetStatusChangeModal}
        toggle={() => setShowAssetStatusChangeModal(false)}
        asset={ticketData?.asset}
        workOrderId={ticketData?.id}
        onSuccess={() => {
          setShowAssetStatusChangeModal(false);
          getData();
        }}
      />

      {/* Tool Checkout Modal */}
      <ToolCheckoutModal
        isOpen={toolCheckoutModal}
        toggle={() => setToolCheckoutModal(false)}
        workOrderId={ticketData?.id}
        onSuccess={handleToolCheckoutSuccess}
      />

      {/* Tool Checkin Modal */}
      <ToolCheckinModal
        isOpen={toolCheckinModal}
        toggle={() => { setToolCheckinModal(false); setToolCheckinId(null); }}
        checkoutId={toolCheckinId}
        onSuccess={handleToolCheckinSuccess}
      />
    </>
  );
};

export default TicketDetailsModal;